/**
 * @file 基于window scroll的 动态render组件
 * @author liudy
 * @desc 参考 @see https://github.com/jameslnewell/react-lazily-render
 */

import { $window, isPassiveListenerSupported } from '@umu/umu-dom';
import React, { Component, createRef, RefObject, PropsWithChildren } from 'react';
import { IBounds } from '@umu/umu-types';
import getElementBounds from './utils/getElementBounds';
import getViewportBounds from './utils/getViewportBounds';
import isElementInViewport from './utils/isElementInViewport';

function isVisible(e?: HTMLElement) {
    return e && !!(e.offsetWidth || e.offsetHeight || e.getClientRects().length);
}

export interface ILazyRenderProps extends PropsWithChildren<{}> {
    className?: string;
    placeholder?: JSX.Element;
    content?: JSX.Element;
    getContainer?: () => Element | Window;
}

export interface ILazyRenderState {
    hasBeenScrolledIntoView: boolean;
}

/**
 * 已废弃，请使用 `import LazyRender from 'common/src/components/lazyRender'`
 */
export default class LazyRender extends Component<ILazyRenderProps, ILazyRenderState> {
    public element?: HTMLElement;
    public container: Window | Element = $window;
    public lazyWrapRef: RefObject<HTMLDivElement>;

    public state = {
        hasBeenScrolledIntoView: false,
    };

    constructor(props: ILazyRenderProps) {
        super(props);

        this.lazyWrapRef = createRef();
    }

    public getContainer(): Window | Element {
        if (typeof this.props.getContainer === 'function') {
            return this.props.getContainer() || $window;
        }
        return $window;
    }

    public getViewportBounds(): IBounds {
        return getViewportBounds(this.container as HTMLElement);
    }

    public getElementBounds(): IBounds {
        if (this.element) {
            return getElementBounds(this.element);
        }
        return {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
        };
    }

    public startListening() {
        const passive = isPassiveListenerSupported() ? { passive: true } : false;
        const container = this.container;
        if (container) {
            container.addEventListener('scroll', this.update, passive);
        }
        window.addEventListener('resize', this.update);
    }

    public stopListening() {
        const container = this.container;
        if (container) {
            container.removeEventListener('scroll', this.update, false);
        }
        window.removeEventListener('resize', this.update);
    }

    public update = () => {
        if (!isVisible(this.element)) {
            return;
        }
        const elementBounds = this.getElementBounds();
        const viewportBounds = this.getViewportBounds();

        if (!elementBounds || !viewportBounds) {
            return;
        }

        if (isElementInViewport(elementBounds, viewportBounds)) {
            this.stopListening();
            this.setState({
                hasBeenScrolledIntoView: true,
            });
        }
    };

    public componentDidMount() {
        if (this.lazyWrapRef.current) {
            this.element = this.lazyWrapRef.current;
            this.container = this.getContainer();
            this.update();
            this.startListening();
        }
    }

    public componentWillUnmount() {
        this.stopListening();
    }

    public renderChildren() {
        const { placeholder, content, children } = this.props;
        const { hasBeenScrolledIntoView } = this.state;

        if (!hasBeenScrolledIntoView && placeholder) {
            return placeholder;
        }

        if (hasBeenScrolledIntoView && content) {
            return content;
        }

        if (hasBeenScrolledIntoView && children) {
            return children;
        }

        return null;
    }

    public render() {
        const { className } = this.props;
        return (
            <div className={className} ref={this.lazyWrapRef}>
                {this.renderChildren()}
            </div>
        );
    }
}
